import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAuth } from '@/contexts/AuthContext';
import { 
  FileSpreadsheet, Download, Upload, CheckCircle, AlertCircle,
  ArrowLeft, ChevronRight, Package, Store, HelpCircle, Lightbulb,
  FileDown, FileUp, Table, Palette, Ruler, Tag
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ExcelTutorial = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [downloading, setDownloading] = useState(null);
  const isUSA = user?.country === 'US';
  const isPro = user?.subscription_plan === 'pro' || user?.subscription_plan === 'sponsor' || user?.role === 'admin';

  const downloadTemplate = async (type) => {
    setDownloading(type);
    try {
      const endpoint = type === 'catalog' 
        ? `${API_URL}/api/products/template/excel`
        : `${API_URL}/api/stores/my/products/template/excel`;
      
      const response = await fetch(endpoint, {
        credentials: 'include'
      });
      
      if (!response.ok) throw new Error('Error al descargar');
      
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = type === 'catalog' ? 'plantilla_catalogo.xlsx' : 'plantilla_tienda.xlsx';
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Download error:', error);
    } finally {
      setDownloading(null);
    }
  };

  const StepCard = ({ number, title, description, icon: Icon, tips }) => (
    <div className="flex gap-4 p-4 bg-white rounded-lg border hover:shadow-md transition-shadow">
      <div className="flex-shrink-0 w-10 h-10 bg-indigo-600 text-white rounded-full flex items-center justify-center font-bold">
        {number}
      </div>
      <div className="flex-1">
        <div className="flex items-center gap-2 mb-1">
          <Icon className="w-5 h-5 text-indigo-600" />
          <h3 className="font-semibold text-slate-800">{title}</h3>
        </div>
        <p className="text-slate-600 text-sm mb-2">{description}</p>
        {tips && (
          <div className="flex items-start gap-2 p-2 bg-amber-50 rounded text-xs text-amber-800">
            <Lightbulb className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span>{tips}</span>
          </div>
        )}
      </div>
    </div>
  );

  const FieldTable = ({ fields }) => (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead className="bg-slate-100">
          <tr>
            <th className="text-left p-2 font-medium">{isUSA ? 'Field' : 'Campo'}</th>
            <th className="text-left p-2 font-medium">{isUSA ? 'Required' : 'Requerido'}</th>
            <th className="text-left p-2 font-medium">{isUSA ? 'Example' : 'Ejemplo'}</th>
          </tr>
        </thead>
        <tbody>
          {fields.map((field, idx) => (
            <tr key={idx} className="border-b">
              <td className="p-2 font-mono text-indigo-600">{field.name}</td>
              <td className="p-2">
                {field.required ? (
                  <Badge className="bg-red-100 text-red-700">*</Badge>
                ) : (
                  <span className="text-slate-400">-</span>
                )}
              </td>
              <td className="p-2 text-slate-600">{field.example}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );

  const catalogFields = [
    { name: 'nombre', required: true, example: 'Gabardina Stretch Premium' },
    { name: 'categoria', required: true, example: 'telas' },
    { name: 'subcategoria', required: true, example: 'gabardinas' },
    { name: 'descripcion', required: true, example: 'Tela de alta calidad...' },
    { name: 'composicion', required: false, example: '97% Algodón, 3% Elastano' },
    { name: 'peso_gsm', required: false, example: '280' },
    { name: 'ancho_cm', required: false, example: '150' },
    { name: 'colores', required: false, example: 'Negro,Azul,Beige' },
    { name: 'moq', required: false, example: '50' },
    { name: 'unidad', required: false, example: 'Mt, Yd, Kg, pzas' },
    { name: 'precio', required: false, example: '85.00' },
    { name: 'moneda', required: false, example: 'MXN o USD' },
  ];

  const storeFields = [
    { name: 'nombre', required: true, example: 'Blusa Floral Primavera' },
    { name: 'categoria', required: true, example: 'ropa' },
    { name: 'descripcion', required: false, example: 'Blusa de algodón...' },
    { name: 'precio', required: false, example: '299' },
    { name: 'moq', required: false, example: '10' },
    { name: 'tags', required: false, example: 'casual,primavera' },
  ];

  const inventoryFields = [
    { name: 'nombre_producto', required: true, example: 'Blusa Floral Primavera' },
    { name: 'color', required: false, example: 'Rosa' },
    { name: 'talla', required: false, example: 'M' },
    { name: 'modelo', required: false, example: 'Slim' },
    { name: 'sku', required: false, example: 'BF-ROSA-M' },
    { name: 'stock', required: true, example: '25' },
    { name: 'precio_adicional', required: false, example: '0' },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 to-white">
      {/* Header */}
      <div className="bg-indigo-600 text-white">
        <div className="max-w-6xl mx-auto px-4 py-8">
          <Button 
            variant="ghost" 
            className="text-white/80 hover:text-white mb-4"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            {isUSA ? 'Back' : 'Volver'}
          </Button>
          
          <div className="flex items-center gap-3 mb-2">
            <FileSpreadsheet className="w-8 h-8" />
            <h1 className="text-2xl font-bold">
              {isUSA ? 'Excel Import Tutorial' : 'Tutorial de Importación Excel'}
            </h1>
          </div>
          <p className="text-indigo-100">
            {isUSA 
              ? 'Learn how to bulk upload products and manage inventory with Excel templates'
              : 'Aprende cómo subir productos masivamente y gestionar inventario con plantillas Excel'
            }
          </p>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 py-8">
        {/* PRO Badge Notice */}
        {!isPro && (
          <Card className="mb-6 border-amber-200 bg-amber-50">
            <CardContent className="p-4 flex items-center gap-3">
              <AlertCircle className="w-5 h-5 text-amber-600" />
              <div className="flex-1">
                <p className="text-amber-800 font-medium">
                  {isUSA ? 'PRO Feature' : 'Función PRO'}
                </p>
                <p className="text-amber-700 text-sm">
                  {isUSA 
                    ? 'Bulk Excel import is available for PRO users. Upgrade to unlock.'
                    : 'La importación masiva por Excel está disponible para usuarios PRO. Actualiza tu plan para desbloquear.'
                  }
                </p>
              </div>
              <Button onClick={() => navigate('/pricing')} className="bg-amber-600 hover:bg-amber-700">
                {isUSA ? 'Upgrade' : 'Actualizar'}
              </Button>
            </CardContent>
          </Card>
        )}

        <Tabs defaultValue="catalog" className="space-y-6">
          <TabsList className="grid w-full grid-cols-2 lg:w-auto lg:inline-grid">
            <TabsTrigger value="catalog" className="gap-2">
              <Package className="w-4 h-4" />
              {isUSA ? 'Catalog Products' : 'Productos de Catálogo'}
            </TabsTrigger>
            <TabsTrigger value="store" className="gap-2">
              <Store className="w-4 h-4" />
              {isUSA ? 'Store Products' : 'Productos de Tienda'}
            </TabsTrigger>
          </TabsList>

          {/* CATALOG TAB */}
          <TabsContent value="catalog" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Package className="w-5 h-5 text-indigo-600" />
                  {isUSA ? 'Catalog Product Import' : 'Importación de Productos de Catálogo'}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Download Button */}
                <div className="flex flex-col sm:flex-row gap-4 p-4 bg-indigo-50 rounded-lg">
                  <div className="flex-1">
                    <h3 className="font-semibold text-indigo-900 mb-1">
                      {isUSA ? 'Step 1: Download the template' : 'Paso 1: Descarga la plantilla'}
                    </h3>
                    <p className="text-sm text-indigo-700">
                      {isUSA 
                        ? 'The template includes example products and field instructions.'
                        : 'La plantilla incluye productos de ejemplo e instrucciones de cada campo.'
                      }
                    </p>
                  </div>
                  <Button 
                    onClick={() => downloadTemplate('catalog')}
                    disabled={downloading === 'catalog'}
                    className="bg-indigo-600 hover:bg-indigo-700"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    {downloading === 'catalog' 
                      ? (isUSA ? 'Downloading...' : 'Descargando...') 
                      : (isUSA ? 'Download Template' : 'Descargar Plantilla')
                    }
                  </Button>
                </div>

                {/* Steps */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-slate-800">
                    {isUSA ? 'How to use:' : 'Cómo usar:'}
                  </h3>
                  
                  <StepCard 
                    number={1}
                    icon={FileDown}
                    title={isUSA ? 'Download template' : 'Descarga la plantilla'}
                    description={isUSA 
                      ? 'Click the button above to download the Excel template with examples.'
                      : 'Haz clic en el botón de arriba para descargar la plantilla Excel con ejemplos.'
                    }
                  />
                  
                  <StepCard 
                    number={2}
                    icon={Table}
                    title={isUSA ? 'Fill in your products' : 'Llena tus productos'}
                    description={isUSA 
                      ? 'Delete the example rows and add your own products. Required fields are marked with *.'
                      : 'Elimina las filas de ejemplo y agrega tus propios productos. Los campos obligatorios están marcados con *.'
                    }
                    tips={isUSA 
                      ? 'Separate colors with commas: "Black,Blue,Red"'
                      : 'Separa los colores con comas: "Negro,Azul,Rojo"'
                    }
                  />
                  
                  <StepCard 
                    number={3}
                    icon={FileUp}
                    title={isUSA ? 'Upload the file' : 'Sube el archivo'}
                    description={isUSA 
                      ? 'Go to Dashboard → My Products → Import button, and select your Excel file.'
                      : 'Ve a Dashboard → Mis Productos → botón Importar, y selecciona tu archivo Excel.'
                    }
                    tips={isUSA 
                      ? 'If a product with the same name exists, it will be UPDATED instead of duplicated.'
                      : 'Si un producto con el mismo nombre ya existe, será ACTUALIZADO en lugar de duplicarse.'
                    }
                  />
                  
                  <StepCard 
                    number={4}
                    icon={CheckCircle}
                    title={isUSA ? 'Add images' : 'Agrega imágenes'}
                    description={isUSA 
                      ? 'After import, edit each product to upload photos from the product detail page.'
                      : 'Después de importar, edita cada producto para subir fotos desde la página de detalle del producto.'
                    }
                  />
                </div>

                {/* Field Reference */}
                <div>
                  <h3 className="font-semibold text-slate-800 mb-3 flex items-center gap-2">
                    <HelpCircle className="w-4 h-4" />
                    {isUSA ? 'Field Reference' : 'Referencia de Campos'}
                  </h3>
                  <FieldTable fields={catalogFields} />
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          {/* STORE TAB */}
          <TabsContent value="store" className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Store className="w-5 h-5 text-emerald-600" />
                  {isUSA ? 'Store Product & Inventory Import' : 'Importación de Productos e Inventario de Tienda'}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Download Button */}
                <div className="flex flex-col sm:flex-row gap-4 p-4 bg-emerald-50 rounded-lg">
                  <div className="flex-1">
                    <h3 className="font-semibold text-emerald-900 mb-1">
                      {isUSA ? 'Step 1: Download the template' : 'Paso 1: Descarga la plantilla'}
                    </h3>
                    <p className="text-sm text-emerald-700">
                      {isUSA 
                        ? 'This template has 2 sheets: Products and Inventory (variants).'
                        : 'Esta plantilla tiene 2 hojas: Productos e Inventario (variantes).'
                      }
                    </p>
                  </div>
                  <Button 
                    onClick={() => downloadTemplate('store')}
                    disabled={downloading === 'store'}
                    className="bg-emerald-600 hover:bg-emerald-700"
                  >
                    <Download className="w-4 h-4 mr-2" />
                    {downloading === 'store' 
                      ? (isUSA ? 'Downloading...' : 'Descargando...') 
                      : (isUSA ? 'Download Template' : 'Descargar Plantilla')
                    }
                  </Button>
                </div>

                {/* Two-sheet explanation */}
                <div className="grid md:grid-cols-2 gap-4">
                  <Card className="border-emerald-200">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base flex items-center gap-2">
                        <Package className="w-4 h-4 text-emerald-600" />
                        {isUSA ? 'Sheet 1: Products' : 'Hoja 1: Productos'}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="text-sm text-slate-600">
                      <p className="mb-2">
                        {isUSA 
                          ? 'Basic product information. If product exists, it gets updated.'
                          : 'Información básica del producto. Si ya existe, se actualiza.'
                        }
                      </p>
                      <FieldTable fields={storeFields} />
                    </CardContent>
                  </Card>
                  
                  <Card className="border-blue-200">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base flex items-center gap-2">
                        <Palette className="w-4 h-4 text-blue-600" />
                        {isUSA ? 'Sheet 2: Inventory' : 'Hoja 2: Inventario'}
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="text-sm text-slate-600">
                      <p className="mb-2">
                        {isUSA 
                          ? 'Detailed stock per variant. One row = one color/size/model combo.'
                          : 'Stock detallado por variante. Una fila = una combinación color/talla/modelo.'
                        }
                      </p>
                      <FieldTable fields={inventoryFields} />
                    </CardContent>
                  </Card>
                </div>

                {/* Inventory Example */}
                <div className="p-4 bg-blue-50 rounded-lg">
                  <h4 className="font-semibold text-blue-900 mb-2 flex items-center gap-2">
                    <Lightbulb className="w-4 h-4" />
                    {isUSA ? 'Inventory Example' : 'Ejemplo de Inventario'}
                  </h4>
                  <p className="text-sm text-blue-800 mb-3">
                    {isUSA 
                      ? 'For a t-shirt with 3 colors and 3 sizes, create 9 rows in the Inventory sheet:'
                      : 'Para una playera con 3 colores y 3 tallas, crea 9 filas en la hoja Inventario:'
                    }
                  </p>
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs bg-white rounded">
                      <thead className="bg-blue-100">
                        <tr>
                          <th className="p-2 text-left">nombre_producto</th>
                          <th className="p-2 text-left">color</th>
                          <th className="p-2 text-left">talla</th>
                          <th className="p-2 text-left">stock</th>
                        </tr>
                      </thead>
                      <tbody className="text-slate-600">
                        <tr><td className="p-2">Playera Básica</td><td className="p-2">Blanco</td><td className="p-2">S</td><td className="p-2">50</td></tr>
                        <tr className="bg-blue-50"><td className="p-2">Playera Básica</td><td className="p-2">Blanco</td><td className="p-2">M</td><td className="p-2">80</td></tr>
                        <tr><td className="p-2">Playera Básica</td><td className="p-2">Blanco</td><td className="p-2">L</td><td className="p-2">60</td></tr>
                        <tr className="bg-blue-50"><td className="p-2">Playera Básica</td><td className="p-2">Negro</td><td className="p-2">S</td><td className="p-2">45</td></tr>
                        <tr><td className="p-2">...</td><td className="p-2">...</td><td className="p-2">...</td><td className="p-2">...</td></tr>
                      </tbody>
                    </table>
                  </div>
                  <p className="text-xs text-blue-700 mt-2">
                    {isUSA 
                      ? '→ Total stock is calculated automatically by summing all variants.'
                      : '→ El stock total se calcula automáticamente sumando todas las variantes.'
                    }
                  </p>
                </div>

                {/* Steps */}
                <div className="space-y-4">
                  <h3 className="font-semibold text-slate-800">
                    {isUSA ? 'How to use:' : 'Cómo usar:'}
                  </h3>
                  
                  <StepCard 
                    number={1}
                    icon={FileDown}
                    title={isUSA ? 'Download template' : 'Descarga la plantilla'}
                    description={isUSA 
                      ? 'Click the button above to download the Excel file with 3 sheets.'
                      : 'Haz clic en el botón de arriba para descargar el archivo Excel con 3 hojas.'
                    }
                  />
                  
                  <StepCard 
                    number={2}
                    icon={Package}
                    title={isUSA ? 'Fill Sheet 1: Products' : 'Llena Hoja 1: Productos'}
                    description={isUSA 
                      ? 'Add your products with basic info. Delete example rows first.'
                      : 'Agrega tus productos con información básica. Elimina las filas de ejemplo primero.'
                    }
                  />
                  
                  <StepCard 
                    number={3}
                    icon={Ruler}
                    title={isUSA ? 'Fill Sheet 2: Inventory' : 'Llena Hoja 2: Inventario'}
                    description={isUSA 
                      ? 'Add one row for each color/size/model combination with its stock quantity.'
                      : 'Agrega una fila por cada combinación de color/talla/modelo con su cantidad de stock.'
                    }
                    tips={isUSA 
                      ? 'The product_name must match EXACTLY with Sheet 1'
                      : 'El nombre_producto debe coincidir EXACTAMENTE con la Hoja 1'
                    }
                  />
                  
                  <StepCard 
                    number={4}
                    icon={FileUp}
                    title={isUSA ? 'Upload the file' : 'Sube el archivo'}
                    description={isUSA 
                      ? 'Go to Dashboard → My Store → Import button, and select your Excel file.'
                      : 'Ve a Dashboard → Mi Tienda → botón Importar, y selecciona tu archivo Excel.'
                    }
                  />
                </div>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* FAQ */}
        <Card className="mt-8">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <HelpCircle className="w-5 h-5 text-slate-600" />
              {isUSA ? 'Frequently Asked Questions' : 'Preguntas Frecuentes'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <h4 className="font-medium text-slate-800">
                {isUSA ? 'Can I update existing products?' : '¿Puedo actualizar productos existentes?'}
              </h4>
              <p className="text-sm text-slate-600">
                {isUSA 
                  ? 'Yes! If a product with the same name exists, it will be updated instead of creating a duplicate.'
                  : '¡Sí! Si un producto con el mismo nombre ya existe, será actualizado en lugar de crear un duplicado.'
                }
              </p>
            </div>
            <div>
              <h4 className="font-medium text-slate-800">
                {isUSA ? 'Can I upload images via Excel?' : '¿Puedo subir imágenes por Excel?'}
              </h4>
              <p className="text-sm text-slate-600">
                {isUSA 
                  ? 'No, images must be uploaded manually after import through the product edit page.'
                  : 'No, las imágenes deben subirse manualmente después de importar, desde la página de edición del producto.'
                }
              </p>
            </div>
            <div>
              <h4 className="font-medium text-slate-800">
                {isUSA ? 'What happens if I make an error?' : '¿Qué pasa si cometo un error?'}
              </h4>
              <p className="text-sm text-slate-600">
                {isUSA 
                  ? 'The import shows detailed results including any errors. You can fix the Excel and re-upload.'
                  : 'La importación muestra resultados detallados incluyendo errores. Puedes corregir el Excel y volver a subirlo.'
                }
              </p>
            </div>
          </CardContent>
        </Card>

        {/* CTA */}
        <div className="mt-8 text-center">
          <p className="text-slate-600 mb-4">
            {isUSA ? 'Ready to import your products?' : '¿Listo para importar tus productos?'}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button onClick={() => navigate('/dashboard/products')} variant="outline">
              <Package className="w-4 h-4 mr-2" />
              {isUSA ? 'Go to My Products' : 'Ir a Mis Productos'}
            </Button>
            <Button onClick={() => navigate('/dashboard/store')} className="bg-emerald-600 hover:bg-emerald-700">
              <Store className="w-4 h-4 mr-2" />
              {isUSA ? 'Go to My Store' : 'Ir a Mi Tienda'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExcelTutorial;
